// defaultDateRange.js — the "last N days" default of a list page's date filter
// (owner 2026-10-20: /seller/orders 7 days, /seller/payment-report 3 days).
//
// Days are Asia/Bangkok calendar days (UTC+7 all year, no DST), sent as
// YYYY-MM-DD; the BE reads date_from as 00:00:00 and date_to as 23:59:59 of
// that Bangkok day. "Last 7 days" = today-6 … today (today counts).
//
// Rules shared by both pages (spec awards-i18n R6 / P2):
//   * applied once, on the page's first load;
//   * a persisted value wins (a date_from / date_to already in the URL);
//   * a URL that carries a search term (order_q, …) gets no default, so a
//     deep-link search still finds an old record;
//   * the user can change or clear it, and a cleared range stays cleared for
//     the life of the page (nothing re-applies the default) until a reload.
//
// Pure + dependency-free on purpose: mdf-bo-fn ports this file as is.

export const ORDERS_DEFAULT_DAYS = 7;
export const PAYMENT_REPORT_DEFAULT_DAYS = 3;

const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function toMs(now) {
    if (now instanceof Date) return now.getTime();
    if (typeof now === "number") return now;
    return Date.now();
}

/** The Bangkok calendar day of `now` shifted by `offsetDays`, as YYYY-MM-DD. */
export function bangkokDay(now, offsetDays) {
    const ms = toMs(now) + BANGKOK_OFFSET_MS + (Number(offsetDays) || 0) * DAY_MS;
    return new Date(ms).toISOString().slice(0, 10);
}

/**
 * defaultDateRange(days, now) → { date_from, date_to }: the last `days` Bangkok
 * days ending today. days < 1 (or junk) is treated as 1 (today only).
 */
export function defaultDateRange(days, now) {
    const n = Math.max(1, Math.floor(Number(days)) || 1);
    return { date_from: bangkokDay(now, -(n - 1)), date_to: bangkokDay(now, 0) };
}

/** Does a persisted state (a `$route.query`) already carry a valid date? */
export function hasPersistedDate(query) {
    const q = query || {};
    const ok = v => DATE_RE.test(String(Array.isArray(v) ? v[0] : v === undefined || v === null ? "" : v).trim());
    return ok(q.date_from) || ok(q.date_to);
}

/**
 * The search params of /seller/orders (sellerOrderFilters TEXT_FIELDS). A link
 * that searches (e.g. ?order_q=MF-123) must still find an OLD order, so it
 * opens without the date default (coordinator ruling 2026-10-20).
 */
export const ORDER_SEARCH_PARAMS = ["order_q", "customer_q", "product_q"];

/** Does `query` carry a non-blank value for any of `searchKeys`? */
export function hasSearchQuery(query, searchKeys) {
    const q = query || {};
    return (searchKeys || []).some(k => {
        const v = Array.isArray(q[k]) ? q[k][0] : q[k];
        return v !== undefined && v !== null && String(v).trim() !== "";
    });
}

/**
 * shouldApplyDefaultDate(query, searchKeys) — first load only: true when the
 * persisted state has neither a date nor a search term.
 */
export function shouldApplyDefaultDate(query, searchKeys) {
    return !hasPersistedDate(query) && !hasSearchQuery(query, searchKeys);
}

/**
 * withDefaultDateRange(filters, days, query, now) — a copy of `filters` with
 * the default range filled in, unless `query` (the persisted state) already
 * has a date. Never mutates `filters`.
 */
export function withDefaultDateRange(filters, days, query, now) {
    const out = Object.assign({}, filters || {});
    if (hasPersistedDate(query)) return out;
    return Object.assign(out, defaultDateRange(days, now));
}

export default {
    ORDERS_DEFAULT_DAYS,
    PAYMENT_REPORT_DEFAULT_DAYS,
    bangkokDay,
    defaultDateRange,
    ORDER_SEARCH_PARAMS,
    hasPersistedDate,
    hasSearchQuery,
    shouldApplyDefaultDate,
    withDefaultDateRange
};
